import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { useAppBranding } from '../../../config/branding';
import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import stationManagement from '../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Revenue Report Chart
 * @returns {any} The Revenue Report Chart component.
 */
function RevenueReportChart() {
  const branding = useAppBranding();
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#00A79D',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns {string} The tooltip title string.
           */
          title: () => t('revenue', 'Revenue').concat(` (${branding.text.currency})`),
          /**
           * Formats the tooltip label using locale string.
           * @param context - The tooltip context object.
           * @returns The formatted label string.
           */
          label: (context: any) => `${context.raw.toLocaleString()} ${branding.text.currency}`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  /**
   * Fetch power consumed data based on time range
   * @param {'last24hours' | 'last7days' | 'lastMonth' | 'last12months'} range The time period range.
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setLoading(true);
    try {
      const response = await stationManagement.fetchRevenueChart({
        timePeriod: range,
        type: 'revenue',
        chargingStation: selectedChargingPoint?.id,
      });
      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  const labels = barGraphData.map((d: any) => d.label);
  const values = barGraphData.map((d: any) => d.total || 0);

  return (
    <div className="report-revenue-chart-wrap">
      <div className="report-revenue-container" style={{ width: '700px' }}>
        <div className="report-revenue-header">
          <div className="report-revenue-title"> {t('revenue')}</div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration: { label: any }) => t(duration.label))}
            onToggle={(index) =>
              fetchPowerConsumedReport(chartTimePeriod[index]?.value || 'last24hours')
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        <div className="report-revenue-chart-container">
          <div className="report-revenue-chart">
            {loading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer height={250}>
                <Line
                  data={{
                    labels,
                    datasets: [
                      {
                        label: 'Power Consumed',
                        data: values,
                        borderColor: '#00A79D',
                        backgroundColor: '#00A79D',
                        fill: false,
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                        pointBackgroundColor: '#00A79D',
                        pointBorderColor: '#00A79D',
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RevenueReportChart;
